import { Box, Button, Typography } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { t } from "i18next";
import { CustomAutoComplete } from "../../../components/input/CustomAutoComplete";
import { EcoEfficiencyDashboardContext } from "../../../contexts/EcoEfficiencyDashboardContext";
import i18n from "../../../i18n";
import { MouseOverPopover } from "../../../components/popper/MouseOverPopover";
import CardReceive from "../../../components/icon/CardReceive";
import { useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { MenuData } from "../../../components/input/CustomSelect";
import { useNavigate } from "react-router-dom";
import { EcoEfficiencyV2 } from "../../../models/dto/response/ResponseEcoEfficiencyV2";
import { useAuth } from "../../../contexts/UserContext";
import { OrganizationContext } from "../../../contexts/OrganizationContext";
import { MultipleAutoComplete } from "../../../components/input/MutipleAutoComplete";
import { EconomicIndicator } from "../../../models/dto/response/ResponseCreateEconomicIndicators";
import { isEcoFSubOrgOpen } from "../../../config/config";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const EcoEfficiencyDashboardFilter = () => {
  const navigate = useNavigate();

  const {
    ecoEfficiencyDashboardFilterForm,
    ecoEfficiency,
    setSelectedEcoEfficiency,
    setSubOrganizations,
    indicators,
    selectedEcoEfficiency,
  } = EcoEfficiencyDashboardContext();

  const { organization, isAdmin } = useAuth();
  const organizationContext = useContext(OrganizationContext);

  // --- Watch Form Values ---
  const selectedIndicatorId =
    ecoEfficiencyDashboardFilterForm.watch("indicator");
  const period = ecoEfficiencyDashboardFilterForm.watch("period");
  const organizationType =
    ecoEfficiencyDashboardFilterForm.watch("organizationType");
  const subOrganizationsFormValue =
    ecoEfficiencyDashboardFilterForm.watch("subOrganizations");

  // --- Local State ---
  const [subOrganizationsCanSelect, setSubOrganizationsCanSelect] = useState<
    string[]
  >([]);

  // --- 0. Organization Type Options Logic ---
  const organizationTypeOptions = useMemo(() => {
    if (!isAdmin()) {
      return [
        {
          nameEN: t("Sub-Organizations"),
          nameTH: t("Sub-Organizations"),
          value: "sub-organization",
        },
      ];
    }

    return isEcoFSubOrgOpen
      ? [
          {
            nameEN: t("Organization"),
            nameTH: t("Organization"),
            value: "organization",
          },
          {
            nameEN: t("Sub-Organizations"),
            nameTH: t("Sub-Organizations"),
            value: "sub-organization",
          },
        ]
      : [
          {
            nameEN: t("Organization"),
            nameTH: t("Organization"),
            value: "organization",
          },
        ];
  }, [isAdmin]);

  // Force default organization type for non-admins
  useEffect(() => {
    if (!organization) return;
    if (!isAdmin()) {
      const currentType =
        ecoEfficiencyDashboardFilterForm.getValues("organizationType");
      if (currentType !== "sub-organization") {
        ecoEfficiencyDashboardFilterForm.setValue(
          "organizationType",
          "sub-organization",
        );
        ecoEfficiencyDashboardFilterForm.setValue("subOrganizations", []);
        ecoEfficiencyDashboardFilterForm.setValue("indicator", "");
        ecoEfficiencyDashboardFilterForm.setValue("period", "");
        setSelectedEcoEfficiency(undefined);
      }
    }
  }, [
    isAdmin,
    ecoEfficiencyDashboardFilterForm,
    setSelectedEcoEfficiency,
    organization,
  ]);

  // --- 1. Indicator Options ---
  const indicatorOptions: MenuData[] = useMemo(() => {
    const validIndicators = indicators
      .filter(
        (e: EconomicIndicator) =>
          e.isDisable === false || e.isDisable === undefined,
      )
      .map((e: EconomicIndicator) => ({
        nameTH: e.nameTh,
        nameEN: e.nameEn,
        value: e._id,
      }));

    return validIndicators;
  }, [indicators, t]);

  // --- 2. Period Options Logic ---
  const periodOption: MenuData[] = useMemo(() => {
    if (
      !selectedIndicatorId ||
      (organizationType === "sub-organization" &&
        subOrganizationsFormValue.length === 0)
    ) {
      return [];
    }

    const periodMap = new Map<
      string,
      { item: EcoEfficiencyV2; foundSubOrgs: Set<string> }
    >();

    ecoEfficiency.forEach((item) => {
      if (item.economicindicatorsRef._id !== selectedIndicatorId) return;

      if (organizationType === "organization") {
        if (!item.organizationRef) return;
      } else {
        if (!item.suborganizationRef) return;
      }

      const timeKey = `${item.startDate}|${item.endDate}|${item.baseYear}`;

      if (!periodMap.has(timeKey)) {
        periodMap.set(timeKey, { item: item, foundSubOrgs: new Set() });
      }

      // Form เก็บ refId ของ sub-organization (storeValue)
      if (item.suborganizationRef?._id) {
        periodMap.get(timeKey)?.foundSubOrgs.add(item.suborganizationRef._id);
      }
    });

    const validPeriods = Array.from(periodMap.values())
      .filter(({ foundSubOrgs }) => {
        if (organizationType === "organization") return true;

        // subOrganizationsFormValue กับ foundSubOrgs เป็น refId ทั้งคู่
        return subOrganizationsFormValue.every((selectedRefId: string) =>
          foundSubOrgs.has(selectedRefId),
        );
      })
      .map(({ item }) => {
        const label = `${dayjs(item.startDate)
          .locale(i18n.language)
          .format("MMM YYYY")} - ${dayjs(item.endDate)
          .locale(i18n.language)
          .format("MMM YYYY")}`;

        return {
          nameTH: label,
          nameEN: label,
          value: item._id,
          startDate: item.startDate,
        };
      });

    return validPeriods.sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
    );
  }, [
    ecoEfficiency,
    selectedIndicatorId,
    organizationType,
    subOrganizationsFormValue,
    i18n.language,
  ]);

  const displayPeriodOptions = useMemo(() => {
    if (!selectedIndicatorId) return [];

    if (periodOption.length > 0) {
      return periodOption;
    }

    // Data exists but no period matches selected sub-orgs → show mismatch box, not "Add"
    if (ecoEfficiency.length > 0 && subOrganizationsFormValue.length > 0) {
      return [];
    }

    return [
      {
        value: "add",
        nameEN: t("Add-Eco-Efficiency"),
        nameTH: t("Add-Eco-Efficiency"),
      },
    ];
  }, [periodOption, selectedIndicatorId, ecoEfficiency, subOrganizationsFormValue]);

  // --- 3. Effects ---

  // Sync Organization Context
  useEffect(() => {
    if (organizationContext && organizationContext.subOrganizations) {
      setSubOrganizations(organizationContext.subOrganizations);
    }
  }, [organizationContext?.subOrganizations, setSubOrganizations]);

  // Auto-Select Indicator if single option
  useEffect(() => {
    if (subOrganizationsFormValue.length > 0 && indicatorOptions.length === 1) {
      const currentVal =
        ecoEfficiencyDashboardFilterForm.getValues("indicator");
      if (currentVal !== indicatorOptions[0].value) {
        ecoEfficiencyDashboardFilterForm.setValue(
          "indicator",
          indicatorOptions[0].value,
        );
      }
    }
  }, [
    subOrganizationsFormValue,
    indicatorOptions,
    ecoEfficiencyDashboardFilterForm,
  ]);

  const showMismatch =
    ecoEfficiency.length > 0 &&
    !!selectedIndicatorId &&
    subOrganizationsFormValue.length > 0 &&
    periodOption.length === 0;

  // Helper Logic to Update Selectable SubOrgs based on Period
  const updateSubOrgSelectable = (periodId: string, orgType: string) => {
    if (!periodId || periodId === "add") return;

    const item = ecoEfficiency.find((e) => e._id === periodId);
    if (!item) return;

    if (orgType === "sub-organization") {
      const matchingItemsInPeriod = ecoEfficiency.filter(
        (e) =>
          e.baseYear === item.baseYear &&
          e.startDate === item.startDate &&
          e.endDate === item.endDate &&
          e.economicindicatorsRef._id === item.economicindicatorsRef._id &&
          e.suborganizationRef,
      );

      const availableSubOrgRefIds = new Set<string>();
      matchingItemsInPeriod.forEach((f) => {
        if (f.suborganizationRef?._id) {
          availableSubOrgRefIds.add(f.suborganizationRef._id);
        }
      });
      setSubOrganizationsCanSelect(Array.from(availableSubOrgRefIds));
    }
  };

  // Main Period Change Effect
  useEffect(() => {
    if (!period) return;

    if (period === "add") {
      ecoEfficiencyDashboardFilterForm.setValue("period", "");
      navigate("/eco-efficiency/add-data");
      return;
    }

    updateSubOrgSelectable(period, organizationType);

    const item = ecoEfficiency.find((e) => e._id === period);
    if (item && item._id !== selectedEcoEfficiency?._id) {
      setSelectedEcoEfficiency(item);
    }
  }, [
    period,
    ecoEfficiency,
    organizationType,
    selectedEcoEfficiency,
    navigate,
    setSelectedEcoEfficiency,
    ecoEfficiencyDashboardFilterForm,
  ]);

  // Restore SubOrg Selectable state on Mount
  useEffect(() => {
    if (period && ecoEfficiency.length > 0 && organizationType) {
      updateSubOrgSelectable(period, organizationType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ecoEfficiency.length]);

  const renderAlertBox = (color: string, text: string | React.ReactNode) => (
    <Box bgcolor={color} p="8px 12px" borderRadius="8px" m="4px 40px">
      <Typography
        color={color === "var(--red)" ? "white" : "primary"}
        variant="text2"
      >
        {text}
      </Typography>
    </Box>
  );

  return (
    <>
      <Grid2
        xs={12}
        p={"20px 40px"}
        spacing={"20px"}
        container
        disableEqualOverflow
        width={"100%"}
      >
        <Grid2 xs={12} md>
          <CustomAutoComplete
            showLabelInput={false}
            name="organizationType"
            label={t("Organization-Type")}
            info={t("Organization-Type")}
            control={ecoEfficiencyDashboardFilterForm.control}
            required
            placeholder={t("Organization-Type")}
            listMenuItem={organizationTypeOptions}
            disabled={!isAdmin()}
            // 🌟 นำ onChange กลับมาทำงานเฉพาะตอน User กดเปลี่ยน! 🌟
            rules={{
              onChange(event) {
                const value = event.target.value;

                if (!value) {
                  ecoEfficiencyDashboardFilterForm.setValue(
                    "subOrganizations",
                    [],
                  );
                } else {
                  ecoEfficiencyDashboardFilterForm.setValue(
                    "subOrganizations",
                    value === "organization"
                      ? [String(organization!.id)]
                      : [],
                  );
                }

                ecoEfficiencyDashboardFilterForm.setValue("indicator", "");
                ecoEfficiencyDashboardFilterForm.setValue("period", "");
                setSelectedEcoEfficiency(undefined);
              },
            }}
          />
        </Grid2>

        <Grid2
          xs={12}
          md
          marginTop={1}
          sx={{
            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
              minHeight: "40px",
            },
          }}
        >
          <MultipleAutoComplete
            showLabelInput={false}
            rules={{
              // required: t("Organization"),
              onChange(event) {
                if (event.target.value.length === 0) {
                  ecoEfficiencyDashboardFilterForm.setValue("indicator", "");
                  ecoEfficiencyDashboardFilterForm.setValue("period", "");
                  setSelectedEcoEfficiency(undefined);
                }
              },
            }}
            name="subOrganizations"
            control={ecoEfficiencyDashboardFilterForm.control}
            label={
              organizationType === "organization"
                ? `${t("Organization")}`
                : `${t("Sub-Organizations")}`
            }
            info={t("Organization")}
            placeholder={t("Select-Param", { param: t("Organization") })}
            listMenuItem={
              organizationType === "organization"
                ? [
                    {
                      // sentinel: ไม่มี refId ระดับ organization ใช้ id (number) แทน
                      value:
                        organization?.id !== undefined
                          ? String(organization.id)
                          : "",
                      nameEN: organization?.name ?? "",
                      nameTH: organization?.name ?? "",
                    },
                  ]
                : organizationContext?.subOrganizations?.map((sub) => ({
                    nameEN: sub.name,
                    nameTH: sub.name,
                    value: sub.refId,
                    disabled: !period
                      ? false
                      : !subOrganizationsCanSelect.includes(sub.refId),
                  })) ?? []
            }
            storeValue
            disabled={organizationType !== "sub-organization"}
          />
        </Grid2>

        <Grid2 xs={12} md>
          <CustomAutoComplete
            showLabelInput={false}
            rules={{
              // required: t("Indicator"),
              onChange(event) {
                if (!event.target.value) {
                  ecoEfficiencyDashboardFilterForm.setValue("period", "", {
                    shouldValidate: true,
                  });
                  setSelectedEcoEfficiency(undefined);
                }
              },
            }}
            name="indicator"
            control={ecoEfficiencyDashboardFilterForm.control}
            label={t("Indicator")}
            info={t("Indicator")}
            placeholder={t("Select-Param", { param: t("Indicator") })}
            listMenuItem={indicatorOptions}
            disabled={
              organizationType && subOrganizationsFormValue.length > 0
                ? false
                : true
            }
          />
        </Grid2>

        <Grid2 xs={12} md>
          <CustomAutoComplete
            showLabelInput={false}
            // rules={{ required: t("Period") }}
            name="period"
            control={ecoEfficiencyDashboardFilterForm.control}
            label={t("Period")}
            info={t("Period")}
            placeholder={t("Select-Param", { param: t("Period") })}
            listMenuItem={displayPeriodOptions}
            disabled={selectedIndicatorId ? false : true}
          />
        </Grid2>

        <Grid2 xs={12} md="auto" sx={{ marginTop: 1 }}>
          <MouseOverPopover
            children={
              <Button
                variant="contained"
                color="green"
                onClick={() => alert("ok")}
                sx={{ width: { xs: "100%", sm: "auto" }, height: "40px" }}
                disabled
              >
                <CardReceive />
              </Button>
            }
            text={t("Download")}
          />
        </Grid2>
      </Grid2>

      {ecoEfficiency.length === 0 && (
        <Box
          onClick={() => navigate("/eco-efficiency/add-data")}
          sx={{ cursor: "pointer" }}
        >
          {renderAlertBox("var(--red)", t("No-Eco-Efficiency-Data"))}
        </Box>
      )}

      {showMismatch && (
        <Box
          display="flex"
          alignSelf="stretch"
          bgcolor="var(--gray3)"
          m="0px 40px"
          p="8px 12px"
          gap="8px"
          borderRadius="8px"
        >
          <InfoOutlinedIcon color="primary" />
          <Typography color="primary" variant="text2">
            {t("Eco-Efficiency-Period-Mismatch")}
          </Typography>
        </Box>
      )}

      {/* case selectedEcoEfficiency - No data */}
      {!selectedEcoEfficiency &&
        ecoEfficiency.length > 0 &&
        periodOption.length > 0 && (
          <Box flex={1} bgcolor={"var(--white)"}>
            <Box
              display={"flex"}
              alignSelf={"stretch"}
              bgcolor={"var(--gray3)"}
              m={"0px 40px"}
              p={"8px 12px"}
              gap={"8px"}
              borderRadius={"8px"}
            >
              <InfoOutlinedIcon color="primary" />
              <Typography color={"primary"} variant={"text2"}>
                {t("Please-Select-Eco-Efficiency-Data")}
              </Typography>
            </Box>
          </Box>
        )}
    </>
  );
};

export default EcoEfficiencyDashboardFilter;
